import { message } from '@didi-pebble/pebble-design-react';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import envConfig from 'env';

import { ErrnoType, PASSPORT } from '@/config';
import { urlQuery } from '@/utils';
import Omega from '@/lib/omega';

import goPassport from './passport';

export interface CustomRequestConfig extends AxiosRequestConfig {
  /* 是否需要返回完整的response */
  allRes?: boolean;
  /**
   * 接口返回数据：{"status": 0,"data": {},"error": null,}
   * intactData为true时只返回data给调用方
   */
  intactData?: boolean;
  /* 统一显示接口异常Toast 默认为true */
  errorToast?: boolean;
  /* 不抛出错误 */
  allResolve?: boolean;
  /** 返回结果只处理最新发的请求 */
  resLatest?: string;
  /** 请求标识 */
  _reqeustId?: Symbol;
  /** 是否是下载请求 */
  isDownload?: boolean;
}

const requestMap: { [key: string]: Symbol } = {};

export interface CommonRes<T> {
  data: T;
  errmsg?: string;
  errno?: number;
  status?: number;
  traceId?: string;
}

export const baseURL = `${envConfig.FLEET_API_URL}/drvlab`;
const axiosInstance = axios.create({
  withCredentials: true,
  baseURL
});

/**
 * 公共参数
 */
const COMMON_PARAMS: any = {
  headers: {
    // TODO 补充公共请求头
  },
  params: {
    // TODO 补充公共请求参数
  },
  data: {
    // TODO 补充公共请求参数
  }
};
const ErrorCodeKey = 'errno';
const SuccessCode = [0, '0'];

/**
 * 报错埋点处理
 */
export function traceApiError(
  base: any,
  { path = '', params = '', status = 200, code = 0, msg = '', trace_id = '', ...others }
) {
  const loginInfo: any = JSON.parse(sessionStorage.getItem('userAuth') || '{}')?.login_info;

  try {
    Omega.trackEvent('tech_gas_station_api_error', '报错埋点', {
      base: base || path || '',
      path,
      params,
      status,
      code,
      msg,
      trace_id,
      user_id: loginInfo?.uid, // 用户、系统信息收敛至接口内
      username: loginInfo?.email || '',
      os_type: 'PC',
      os_version: '0.0.0',
      ...others
    });
  } catch (err) {
    console.error('omega upload error');
  }
}

/**
 * 统一错误处理
 */
function commonHandleError(response: AxiosResponse, responseData?: any) {
  const config = response?.config as CustomRequestConfig;
  const data = responseData || response?.data;

  // Omega错误上报
  traceApiError(config.baseURL, {
    path: config?.url,
    params: config?.params || config?.data,
    status: response?.status,
    code: data?.errno,
    msg: data?.errmsg,
    trace_id: data?.traceId
  });

  // 其它错误逻辑
  switch (data?.errno || data?.code) {
    case ErrnoType.ErrUserAccountDelete:
    case ErrnoType.ErrTicketInvalid:
      setTimeout(() => {
        goPassport(PASSPORT.login);
      }, 20);
      break;
    case ErrnoType.ErrUSerNoExist:
    case ErrnoType.ErrNoPermission:
    case ErrnoType.ErrUserAccountFreeze: {
      window.location.hash = urlQuery('/noPermission', { type: data.errno });
      break;
    }
    default: {
      if (config?.errorToast) {
        // 显示错误信息
        message.error(data?.errmsg || data?.error || 'error');
      }
      break;
    }
  }
}

/**
 * 请求拦截
 */
axiosInstance.interceptors.request.use(
  (config: any) => {
    const newConfig = {
      ...config,
      errorToast: config?.errorToast !== false
    };
    /* 公共参数处理 */

    newConfig.headers = {
      ...COMMON_PARAMS.headers,
      ...config.headers
    };
    newConfig.params = {
      ...COMMON_PARAMS.params,
      ...config.params
    };
    // 检查 config.data 的类型
    if (config.data instanceof FormData) {
      // 如果是 FormData 对象（上传文件），直接使用
      newConfig.data = config.data;
    } else {
      // 如果是普通的 Object，进行解构操作
      newConfig.data = {
        ...COMMON_PARAMS.data,
        ...config.data
      };
    }

    return newConfig;
  },
  error => {
    return Promise.reject(error);
  }
);

/**
 * 响应拦截
 */
axiosInstance.interceptors.response.use(
  async response => {
    const resData = response.data;
    const config = response?.config as CustomRequestConfig;

    // eslint-disable-next-line no-underscore-dangle
    if (config?.resLatest && config?._reqeustId !== requestMap[config?.resLatest]) {
      return false;
    }
    if (config?.intactData) {
      return resData.data;
    }

    if (config.allResolve) {
      return Promise.resolve(response);
    }

    if (config?.isDownload) {
      if (resData.type === 'application/json') {
        const reader: any = new FileReader(); // 创建一个FileReader实例

        reader.readAsText(response.data, 'utf-8'); // 读取文件,结果用字符串形式表示

        try {
          const resDataOut = await new Promise((resolve, reject) => {
            reader.onload = () => {
              // 读取完成后,**获取reader.result**
              const resData2 = JSON.parse(reader.result || '{}');

              if (resData2?.errno === 0 || resData2?.code === 0) {
                resolve(resData2);
              }
              reject(resData2);
            };
          });

          return resDataOut;
        } catch (error) {
          commonHandleError(response, error);
          return Promise.reject(error);
        }
      }
      return Promise.resolve(response);
    }
    if (SuccessCode.indexOf(resData[ErrorCodeKey]) === -1) {
      commonHandleError(response);
      return Promise.reject(config?.allRes ? response : resData);
    }
    return Promise.resolve(config?.allRes ? response : resData);
  },
  async error => {
    if (error?.response?.config?.isDownload) {
      const response = error?.response;
      const resData = response?.data;

      if (resData.type === 'application/json') {
        const reader: any = new FileReader(); // 创建一个FileReader实例

        reader.readAsText(response?.data, 'utf-8'); // 读取文件,结果用字符串形式表示

        try {
          const e = await new Promise(resolve => {
            reader.onload = () => {
              // 从流里面读取报错结果
              const resData2 = JSON.parse(reader.result || '{}');

              resolve(resData2);
            };
          });

          throw e;
        } catch (e) {
          return Promise.reject(e);
        }
      }
    } else {
      commonHandleError(error);
    }
    return Promise.reject(error);
  }
);
/**
 * 请求服务API
 */
export function Http<T>(config: CustomRequestConfig): Promise<CommonRes<T>> {
  if (config?.resLatest) {
    // eslint-disable-next-line no-underscore-dangle, no-param-reassign
    config._reqeustId = Symbol(config?.resLatest);
    // eslint-disable-next-line no-underscore-dangle
    requestMap[config?.resLatest] = config._reqeustId;
  }
  return axiosInstance(config);
}
